import { Project, TodoList2 } from '@griply/assets';
import { EntityLinks } from '@griply/types';
import {
  buildGoalsById,
  getGoalAncestorIds,
  getGoalLifeArea,
  getLinkedItemColor,
} from '@griply/utils';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  IconFormIcons,
  IconFormNames,
  Search,
  Target,
  X,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { Goal, GoalType } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import * as Styled from './goal-tree-menu.style';
import { GoalTreeMenuItem } from './goal-tree-menu-item';
import {
  GoalTreeMenuSelection,
  resolveGoalTreeItemState,
} from './resolve-goal-tree-item-state';
import { GoalTreeCreateOption, GoalTreeItemIds } from './types';
import { useFilteredGoalTreeItems } from './use-filtered-goal-tree-items';

const createRows = [
  {
    option: GoalTreeCreateOption.LifeArea,
    icon: IconFormIcons[IconFormNames.PlaceholderIcon],
    label: 'goal-tree-menu.create.life-area',
  },
  {
    option: GoalTreeCreateOption.Goal,
    icon: Target,
    label: 'goal-tree-menu.create.goal',
  },
  {
    option: GoalTreeCreateOption.Project,
    icon: Project,
    label: 'goal-tree-menu.create.project',
  },
];

export type { GoalTreeMenuSelection } from './resolve-goal-tree-item-state';

export type GoalTreeMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside' | 'initialFocus'
> & {
  goals: Goal[];
  lifeAreas: LifeArea[];
  // Caller-supplied: a UI menu must not fetch — without it a migrated goal renders as a root.
  links?: EntityLinks;
  /**
   * Reports the resolved target: a goal as its frozen ancestor chain (last entry is
   * the goal itself) with no life area, a life area as `([], lifeAreaId)`, and
   * the "No life area" bucket as `([], null)`.
   */
  onSelect: (ancestorIds: ID[], lifeAreaId: ID | null) => void;
  onClose: () => void;
  /** The already-picked item. Highlighted, and disabled for the two life-area rows. */
  selection?: GoalTreeMenuSelection;
  /** Branch to open on mount when it differs from `selection` — the goal being moved. */
  expandTo?: { goalId?: ID; lifeAreaId?: ID };
  /** Goals that cannot be picked — cycle prevention on a goal move, the current target. */
  disabledGoalIds?: Set<ID>;
  /** Section move: projects cannot receive sections, so they stay listed but disabled. */
  disableProjects?: boolean;
  /** For callers with no "unset" state to report — the bucket goes, its goals rise to level 0. */
  hideNoLifeArea?: boolean;
  // Labels kept caller-owned so this shared menu stays free of feature copy.
  allTasksLabel?: string;
  isAllTasksSelected?: boolean;
  onSelectAllTasks?: () => void;
  /** Offers the typed search text as a new life area / goal / project. */
  onCreate?: (name: string, option: GoalTreeCreateOption) => void;
  isCreatePremium?: Partial<Record<GoalTreeCreateOption, boolean>>;
  clearLabel?: string;
  onClear?: () => void;
  /** Opt-in multi-select: rows toggle and the menu stays open until Done. Absent = single-select. */
  selectedGoalIds?: ID[];
  selectedLifeAreaIds?: ID[];
  onToggleGoal?: (goalId: ID) => void;
  onToggleLifeArea?: (lifeAreaId: ID) => void;
};

const noDisabledGoalIds = new Set<ID>();

export const GoalTreeMenu: React.FC<GoalTreeMenuProps> = ({
  goals,
  lifeAreas,
  links,
  onSelect,
  onClose,
  selection,
  expandTo,
  disabledGoalIds = noDisabledGoalIds,
  disableProjects,
  hideNoLifeArea,
  allTasksLabel,
  isAllTasksSelected,
  onSelectAllTasks,
  onCreate,
  isCreatePremium,
  clearLabel,
  onClear,
  selectedGoalIds,
  selectedLifeAreaIds,
  onToggleGoal,
  onToggleLifeArea,
  ...rest
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchValue, setSearchValue] = useState('');
  const createName = searchValue.trim();
  const showCreate = !!onCreate && !!createName;
  const visibleCreateRows = disableProjects
    ? createRows.filter(({ option }) => option !== GoalTreeCreateOption.Project)
    : createRows;
  const [expandedItems, setExpandedItems] = useState<Set<ID>>(() => {
    // Open the branch the user needs to see: the goal being moved, else the already-picked target.
    const focusGoalId = expandTo?.goalId ?? selection?.goalId;
    const focusLifeAreaId = expandTo?.lifeAreaId ?? selection?.lifeAreaId;

    if (!focusGoalId) {
      return new Set<ID>(focusLifeAreaId ? [focusLifeAreaId] : []);
    }

    const goalsById = buildGoalsById(goals);
    const focusGoal = goalsById.get(focusGoalId);
    const initial = new Set<ID>([
      focusGoalId,
      ...(focusGoal ? getGoalAncestorIds(focusGoal, { goalsById, links }) : []),
    ]);
    const lifeAreaId = focusGoal
      ? getGoalLifeArea(focusGoal, { goalsById, links })
      : undefined;
    initial.add(lifeAreaId ?? GoalTreeItemIds.NoLifeArea);

    return initial;
  });

  useFocusChildrenWithArrows(containerRef);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const multiSelection = useMemo(
    () =>
      selectedGoalIds
        ? { goalIds: selectedGoalIds, lifeAreaIds: selectedLifeAreaIds ?? [] }
        : undefined,
    [selectedGoalIds, selectedLifeAreaIds],
  );

  // What the caller's set currently holds, goals first — each row removes its own link.
  const selectedParents = useMemo(() => {
    if (!multiSelection) {
      return [];
    }
    const selectedGoals = multiSelection.goalIds.flatMap((goalId) => {
      const goal = goals.find(({ id }) => id === goalId);
      return goal
        ? [
            {
              key: `goal-${goal.id}`,
              name: goal.name,
              icon:
                goal.type === GoalType.Project
                  ? Project
                  : goal.iconName
                    ? IconFormIcons[goal.iconName]
                    : Target,
              color: getLinkedItemColor(goal.id, { goals, lifeAreas, links }),
              remove: () => onToggleGoal?.(goal.id),
            },
          ]
        : [];
    });
    const selectedLifeAreas = multiSelection.lifeAreaIds.flatMap(
      (lifeAreaId) => {
        const lifeArea = lifeAreas.find(({ id }) => id === lifeAreaId);
        return lifeArea
          ? [
              {
                key: `life-area-${lifeArea.id}`,
                name: lifeArea.name,
                icon: IconFormIcons[lifeArea.iconName],
                color: lifeArea.color ?? null,
                remove: () => onToggleLifeArea?.(lifeArea.id),
              },
            ]
          : [];
      },
    );
    return [...selectedGoals, ...selectedLifeAreas];
  }, [multiSelection, goals, lifeAreas, links, onToggleGoal, onToggleLifeArea]);

  // Multi-select emits the activated item and stays open; the caller owns the set.
  const onGoal = (ancestorIds: ID[], lifeAreaId?: ID | null) => {
    if (multiSelection) {
      const pickedGoalId = ancestorIds[ancestorIds.length - 1];
      pickedGoalId
        ? onToggleGoal?.(pickedGoalId)
        : lifeAreaId && onToggleLifeArea?.(lifeAreaId);
      return;
    }
    onSelect(ancestorIds, lifeAreaId ?? null);
    onClose();
  };

  const toggleCollapse = (e: React.MouseEvent, id: ID) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const goalIndexMap = useMemo(() => {
    const map = new Map<ID, number>();
    goals.forEach((goal, index) => map.set(goal.id, index));
    return map;
  }, [goals]);

  const filteredItems = useFilteredGoalTreeItems({
    allGoals: goals,
    lifeAreas,
    searchValue,
    expandedItems,
    goalIndexMap,
    hideNoLifeArea,
    links,
  });

  return (
    <Styled.Menu
      {...rest}
      ref={containerRef}
      onOutside={onClose}
      initialFocus={inputRef}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            startIcon={Search}
            value={searchValue}
            placeholder={t('general.labels.search')}
            onChange={onChangeInput}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      {!!selectedParents.length && (
        <PopupMenuList
          hasBottomBorder
          role="group"
          aria-label={t('general.contributes-to')}
        >
          <PopupMenuTitle
            title={`${t('general.contributes-to')}:`}
            isSubTitle
          />
          {selectedParents.map(({ key, name, icon, color, remove }) => (
            <PopupMenuListItem key={key}>
              <PopupMenuButton
                start={
                  <Icon icon={icon} style={{ color: color ?? undefined }} />
                }
                end={<Icon icon={X} />}
                endColor={Colors.TextSecondary}
                onClick={remove}
                tabIndex={0}
                overflowEllipsis
              >
                {name}
              </PopupMenuButton>
            </PopupMenuListItem>
          ))}
        </PopupMenuList>
      )}

      {!!onSelectAllTasks && !searchValue && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={TodoList2} />}
              onClick={onSelectAllTasks}
              isSelected={isAllTasksSelected}
              end={isAllTasksSelected ? <Icon icon={Check} /> : undefined}
              tabIndex={0}
            >
              {allTasksLabel}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder={showCreate || !!onClear}>
        {!!multiSelection && (
          <PopupMenuTitle title={t('general.labels.select')} isSubTitle />
        )}
        {!filteredItems.length && !!searchValue && (
          <PopupMenuNoResults>
            {t('general.labels.no-results')}
          </PopupMenuNoResults>
        )}
        {filteredItems.map((item) => {
          const { isSelected, isDisabled, isParent } = resolveGoalTreeItemState(
            {
              item,
              searchValue,
              selection,
              multiSelection,
              disabledGoalIds,
              disableProjects,
            },
          );

          return (
            <GoalTreeMenuItem
              key={`${item.type}-${item.id}`}
              item={item}
              isSelected={isSelected}
              isDisabled={isDisabled}
              isExpanded={expandedItems.has(item.id)}
              onGoal={onGoal}
              toggleCollapse={toggleCollapse}
              searchValue={searchValue}
              isParent={isParent}
            />
          );
        })}
      </PopupMenuList>

      {showCreate && (
        <PopupMenuList hasBottomBorder={!!onClear}>
          {visibleCreateRows.map(({ option, icon, label }) => {
            const onCreateOption = (e: React.MouseEvent) => {
              e.preventDefault();
              onCreate?.(createName, option);
            };

            return (
              <PopupMenuListItem key={option}>
                <PopupMenuButton
                  start={<Icon icon={icon} />}
                  startColor={Colors.Primary}
                  textColor={Colors.Primary}
                  onClick={onCreateOption}
                  isPremium={isCreatePremium?.[option]}
                  tabIndex={0}
                  overflowEllipsis
                >
                  {t(label, { name: createName })}
                </PopupMenuButton>
              </PopupMenuListItem>
            );
          })}
        </PopupMenuList>
      )}

      {!!onClear && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={X} />}
              startColor={Colors.Error}
              textColor={Colors.Error}
              onClick={onClear}
              tabIndex={0}
            >
              {clearLabel}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
    </Styled.Menu>
  );
};
